import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  downloadDirectSharedVideo,
  getDirectSharedVideo,
  type SharedVideo,
} from '@/features/sharing/services/sharingApi';
import { ArrowLeftIcon, Button, DownloadIcon, ErrorPage, Spinner } from '@/shared/ui';

export default function SharedVideoPage() {
  const { shareId = '' } = useParams<{ shareId: string }>();
  const [video, setVideo] = useState<SharedVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setVideo(null);
    setDownloadError(false);
    void getDirectSharedVideo(shareId)
      .then((result) => {
        if (!active) return;
        setVideo(result);
      })
      .catch(() => {
        if (!active) return;
        setVideo(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [shareId]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      const url = await downloadDirectSharedVideo(shareId);
      window.location.href = url;
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="content-page__loading">
        <Spinner size={26} label="Loading shared video" />
      </div>
    );
  }

  if (!video) {
    return (
      <ErrorPage
        title="Video unavailable"
        message="This shared video is no longer available."
        backLabel="Back to Library"
        backTo="/library"
      />
    );
  }

  return (
    <section className="content-page shared-video-page">
      <div className="content-page__top">
        <Link to="/library" className="content-page__back" aria-label="Back to library">
          <ArrowLeftIcon size={18} />
        </Link>
      </div>

      <header className="content-page__head">
        <h1 className="content-page__title">{video.title}</h1>
        <div className="content-page__head-meta">
          <Button iconLeft={<DownloadIcon size={16} />} onClick={() => void handleDownload()} disabled={downloading}>
            {downloading ? 'Preparing…' : 'Download video'}
          </Button>
        </div>
      </header>

      {downloadError ? (
        <p className="share-page__error" role="alert">
          Could not prepare the download.
        </p>
      ) : null}

      <div className="content-page__layout content-page__layout--theater">
        <section className="content-page__player-col">
          <video className="content-page__video" src={video.videoUrl} poster={video.thumbnailUrl} controls playsInline />
        </section>
      </div>
    </section>
  );
}
